import { useSyncExternalStore } from 'react';
import type { GameId } from '@/lib/types';
import { GAMES } from '@/lib/types';

export type BatteryStatus = 'idle' | 'in_progress' | 'completed' | 'abandoned';

export type BatteryState = {
  battery_id: string;
  started_at: number;
  order: GameId[];
  completed: GameId[];
  current_index: number;
  status: BatteryStatus;
};

const KEY = 'apex.battery.v1';

function fresh(): BatteryState {
  return {
    battery_id: '',
    started_at: 0,
    order: [...GAMES],
    completed: [],
    current_index: 0,
    status: 'idle',
  };
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function read(): BatteryState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return fresh();
    const parsed = JSON.parse(raw) as Partial<BatteryState>;
    if (!parsed || typeof parsed !== 'object') return fresh();
    return {
      battery_id: parsed.battery_id ?? '',
      started_at: typeof parsed.started_at === 'number' ? parsed.started_at : 0,
      order: Array.isArray(parsed.order) && parsed.order.length === GAMES.length ? parsed.order as GameId[] : [...GAMES],
      completed: Array.isArray(parsed.completed) ? (parsed.completed as GameId[]).filter((g) => (GAMES as readonly GameId[]).includes(g)) : [],
      current_index: typeof parsed.current_index === 'number' ? parsed.current_index : 0,
      status: ((['idle', 'in_progress', 'completed', 'abandoned'] as BatteryStatus[]).includes(parsed.status as BatteryStatus) ? parsed.status : 'idle') as BatteryStatus,
    };
  } catch {
    return fresh();
  }
}

let state: BatteryState = read();
const listeners = new Set<() => void>();

function persist() {
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* ignore */ }
  listeners.forEach((l) => l());
}

export function getBattery(): BatteryState {
  return state;
}

export function startBattery(): BatteryState {
  state = {
    battery_id: uuid(),
    started_at: Date.now(),
    order: [...GAMES],
    completed: [],
    current_index: 0,
    status: 'in_progress',
  };
  persist();
  return state;
}

export function resetBattery(): void {
  state = fresh();
  persist();
}

export function abandonBattery(): void {
  if (state.status === 'in_progress') {
    state = { ...state, status: 'abandoned' };
    persist();
  }
}

export function markGameCompleted(game: GameId): BatteryState {
  if (state.status !== 'in_progress') return state;
  const completed = state.completed.includes(game) ? state.completed : [...state.completed, game];
  const next_index = state.order.findIndex((g, i) => i > state.current_index && !completed.includes(g));
  const allDone = completed.length >= state.order.length;
  state = {
    ...state,
    completed,
    current_index: next_index >= 0 ? next_index : state.current_index,
    status: allDone ? 'completed' : 'in_progress',
  };
  persist();
  return state;
}

export function getCurrentBatteryGame(): GameId | null {
  if (state.status !== 'in_progress') return null;
  const remaining = state.order.find((g) => !state.completed.includes(g));
  return remaining ?? null;
}

export function isBatteryComplete(): boolean {
  return state.status === 'completed';
}

export function useBattery(): BatteryState {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => listeners.delete(l); },
    () => state,
    () => state,
  );
}
